<template>
    <modal
        v-if="isActive"
        :show="showModal"
        :showContent="showContent"
        :pageTitle="title"
        :acl="acl"
        :url="url"
        @close="closeModal"
        :closeBtn="closeBtn"
        position="right"
        size='medium'
        id="identify-container"
        class="identify-panel loader-panel"
        classContent="overflow-hidden">
        <template v-slot:content>
            <div class="d-flex flex-column">
                <div class="row">
                    <div class="col">
                        <div class="title"> {{ title }}</div>
                        <hr/>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="row">
                            <div class="col-12">
                                <IdentifyLayer
                                    ref="identifyLayer"
                                    :index="index"
                                    :responses="responses"
                                    :missingResponses="missingResponses"
                                    :emptyResponses="emptyResponses"
                                    :validator="currentValidator"
                                    @onChangeIndex="onChangeIndex"
                                    :disabled="readonlyFormItem || loading"
                                />
                            </div>
                            <div class="col-12" v-if="isCoordinateActive">
                                <IdentifyCoordinates
                                    :coordinate="coordinatesInfo.coordinate"
                                    :projection="coordinatesInfo.projection"
                                    :resolution="coordinatesInfo.resolution"
                                    :disabled="readonlyFormItem || loading"
                                    :eventIdentify="eventIdentify"
                                    :moduleCode="moduleCode"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="panel-control d-flex flex-column flex-fill flex-scroll">
                <div class="panel-control-content d-flex flex-column flex-fill">
                    <div class="row flex-fill">
                        <div class="col">
                             <template v-if="loading">
                                <div class="identify-results loading">
                                    <div class="">
                                        <div class="full-size-loader"></div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <template v-if="loadingError" >
                                    <div class="alert alert-danger" role="alert">
                                        {{ errorMsg }}
                                    </div>
                                </template>
                                <template v-else-if="responses">
                                    <div class="identify-results">
                                        <IdentifyViewer
                                            :index="index"
                                            :responses="responses"
                                            :requests="requests"
                                            :missingResponses="missingResponses"
                                            :format="format"
                                            :activeItemId="activeItemId"
                                            @close="onCloseViewer"
                                        />
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import { v1 as uuidv1 } from 'uuid';
import { mapGetters, mapActions } from "vuex";
import { EVENTS } from '@/app/shared/utils/constants';
import {default as IdentifyInteraction, IdentifyEventType} from '../ol/interaction/Identify';
import ButtonControlMixin from '../mixins/ButtonControlMixin';
import IdentifyMixin from '../shared/identify/IdentifyMixin';
import {isInsideResolutionsLimits} from '../shared/catalog/utils/LayersUtils';
import {
    buildIdentifyRequest,
    defaultQueryableFilter,
    filterRequestParams,
    getValidator as validator,
} from '../shared/catalog/utils/MapInfoUtils';
import {
    getFeatureInfo,
    getFeatureId,
} from '../shared/catalog/utils/IdentifyUtils';

let varsOriginal = {
    interaction: null
};

let vars = Object.assign({}, varsOriginal);

export default {
    name: "IdentifyPanel",
    mixins: [
        ButtonControlMixin,
        IdentifyMixin,
    ],
    components: {
        IdentifyLayer: () => import(/* webpackChunkName: "identify-layer" */ './../shared/identify/components/IdentifyLayerSelector'),
        IdentifyCoordinates: () => import(/* webpackChunkName: "identify-coordinates" */ './../shared/identify/components/IdentifyCoordinates'),
        IdentifyViewer: () => import(/* webpackChunkName: "identify-viewer" */ './../shared/identify/components/IdentifyDefaultViewer'),
        Modal: () => import(/* webpackChunkName: "modal-data" */ '@/app/shared/components/modals/ModalData'),
    },
    props: {
        closeBtn: {
            type: Boolean,
            default: true
        },
        identifySequential: {
            type: Boolean,
            default: false
        },
        eventIdentify: {
            type: String,
            default: EVENTS.MAP_IDENTIFY,
        },
    },
    data() {
        return {
            idPanel: 'identify',
            idPanelCatalog: 'catalog',
            className: 'ol-identify',
            showContent: false,
            lists: {
                loaded: false,
            },
            activeItemId: null,
            result: null,
            options: {
                sourceId: 'vs_identify',
                layerId: 'lyr_identify',
                zindex: 1000,
            },
            coordinatesInfo: {
                coordinate: null,
                projection: null,
                resolution: null,
            }
        }
    },
    mounted() {

    },
    created () {
        this.$eventBus.listen(this.moduleCode, this.eventIdentify, this.getFeatureInfoOnFeatureInfoClick );
    },
    beforeDestroy() {
        this.$eventBus.remove(this.moduleCode, this.eventIdentify, this.getFeatureInfoOnFeatureInfoClick );
    },
    computed: {
        ...mapGetters({
            tocSelectedSelector: 'tocSelectedSelector',
            loading: 'identifyLoadingSelector',
            loadingError: 'identifyLoadingErrorSelector',
            maxItems: 'identifyMaxItemsSelector',
            tolerance: 'identifyToleranceSelector',
            format: 'identifyGeneralInfoFormatSelector',
            index: 'identifyIndexSelector',
            requests: 'identifyRequestsSelector',
            responses: 'identifyResponsesSelector',
            validResponses: 'identifyValidResponsesSelector',
        }),
        showModal() {
            return this.isActive && this.isPanelVisible;
        },
        panelCfg() {
            return this.panel ?? {};
        },
        isCoordinateActive(){
            return this.panelCfg?.show_coordinate ?? false;
        },
        isPanelCatalogVisible(){
            return this.$store.getters[`${this.moduleCode}/isPanelVisible`](this.idPanelCatalog);
        },
        layerSelector() {
            return this.tocSelectedSelector(this.moduleCode);
        },
        readonlyFormItem() {
            return false;
        },
        currentValidator() {
            return validator(this.format);
        },
        missingResponses() {
            return this.requests.length - this.responses.length;
        },
        emptyResponses() {
            let noValidResponses = [];
            if(this.currentValidator){
                noValidResponses = this.currentValidator.getNoValidResponses(this.responses);
            }
            return this.requests.length === noValidResponses.length;
        },
        errorMsg() {
            let defaultError = 'Erro';
            return this.loadingError ?
            (this.loadingError.error && this.loadingError.error.error ?
                this.loadingError.error.error : (this.loadingError.error ? this.loadingError.error : defaultError)) : defaultError;
        },
        actionMap() {
            return this.$store.getters.getActionMap(this.moduleCode);
        },
        defaultInfoFormat() {
            return this.format;
            // const availableInfoFormats = getAvailableInfoFormatValues();
            // return availableInfoFormats.indexOf('application/json') !== -1
            //     ? 'application/json'
            //     : getDefaultInfoFormatValue();
        },
    },
    methods: {
        ...mapActions([
            'setPanel',
        ]),
        setMapInteraction(flag) {
            let interaction = vars.interaction;
            if(flag){
                if(!interaction){
                    interaction = new IdentifyInteraction({});
                    interaction.on(IdentifyEventType.IDENTIFYCOORD, (evt) => {
                        this.$eventBus.publish(
                            this.moduleCode,
                            this.eventIdentify,
                            {
                                coordinate: evt.coordinate,
                                resolution: evt.resolution,
                                projection: evt.projection,
                                params: evt.params
                            }
                        );
                    });
                }
                let map = this.getMap();
                map.addInteraction(interaction);
                vars.interaction = interaction;
            }else{
                if(interaction){
                    let _map = interaction.getMap();
                    if(_map){
                        _map.removeInteraction(interaction);
                    }
                    vars.interaction = null;
                }
            }
        },
        setActiveMapInteraction(flag) {
            let interaction = vars.interaction;
            if(interaction){
                if(!flag && interaction.getActive()){
                    interaction.setActive(false);
                }else if(flag && !interaction.getActive()){
                    interaction.setActive(true);
                }
            }
        },
        initialize() {
            if(this.showContent){
                return;
            }
            this.showContent=true;
            this.$nextTick(() => {
                this.activeTooltip();
            });
        },
        openModal() {
            if(this.isActive && !this.isPanelVisible){
                this.openPanel();
            }
        },
        closeModal() {
            this.reset();
            this.closePanel();
        },
        reset(){
            this.setInfoMarker(null);
            Object.assign(this.$data.coordinatesInfo, this.$options.data().coordinatesInfo);
            // Object.assign(this.$data, this.$options.data());
        },
        onCloseViewer(args={}) {
            this.closeModal();
        },
        onChangeIndex(params){
            if(params?.reset) {
                this.activeItemId = null;
            }
            this.changePage(params?.idx)
        },
        isLayerQueryable(layer) {
            const _map = this.getMap();
            if(!_map) return false;

            const _view = _map.getOLMapView();
            const currentResolution = _view.getResolution();
            return isInsideResolutionsLimits(layer, currentResolution)
                && defaultQueryableFilter(layer);
        },
        async getFeatureInfoOnFeatureInfoClick(params) {
            this.setCoordinatesInfo(params);

            return await new Promise((resolve, reject) => {
                this.activeItemId = null;
                this.setLoading(true);
                this.purgeResults();
                resolve(
                    this.fetchResults(params)
                    .then((result) => {
                        let activeItemId = params.activeItemId || null;
                        if(activeItemId){
                            for (var i = 0; i < this.responses.length; i++) {
                                let resp = this.responses[i];
                                let metadata = resp.layerMetadata;
                                if(metadata && metadata.features){
                                    let featInfo = metadata.featureInfo;
                                    if(featInfo.collapsible && featInfo.property_title){
                                        let feature = metadata.features.find(f => {
                                            return f.properties[featInfo.property_title] == activeItemId;
                                        });
                                        if(feature){
                                            let fid = getFeatureId(feature);
                                            this.activeItemId = fid ? fid+'' : null;
                                            if(this.$refs && this.$refs.identifyLayer){
                                                this.$refs.identifyLayer.setActiveIndexByLabel(metadata.title);
                                            }
                                            break;
                                        }
                                    }
                                }
                            }
                        }
                    }).catch((e) => {
                        this.showError(e);
                        this.setInfoMarker();
                    })
                    .finally(() => {
                        this.setLoading(false);
                    })
                );
            });
        },
        async fetchResults(params) {
            const _map = this.getMap();
            if(!_map) return;

            const _selected = this.layerSelector;
            const layers = _map.getLayers().getArray();
            let queryableLayers = layers.filter((layer) => {
                return this.isLayerQueryable(layer);
            });

            const selectedLayers = _selected ? layers.filter((layer) => layer.get('id') == _selected) : [];
            const queryableSelectedLayers = selectedLayers.filter((layer) => this.isLayerQueryable(layer));
            if (queryableSelectedLayers.length) {
                queryableLayers = queryableSelectedLayers;
            }

            if (queryableLayers.length === 0
                || queryableSelectedLayers.length === 0 && selectedLayers.length !== 0) {
                // // this.purgeResults();
                // this.noQueryableLayers();
                // return;
            }

            this.setInfoMarker(params.coordinate);
            this.openModal();

            const excludeParams = [
                "SLD_BODY",
                "FORMAT",
            ];
            const includeOptions = [
                "buffer",
                "cql_filter",
                "filter",
                "propertyName"
            ];

            const promises = [];
            queryableLayers.map((layer, index) => {
                let reqParams = {
                    format: this.defaultInfoFormat,// this.format,
                    maxItems: this.maxItems,
                    tolerance: this.tolerance,
                    ...params
                };
                let { url, request, metadata } = buildIdentifyRequest(layer, reqParams);

                if (url) {
                    const basePath = url;
                    const requestParams = request;
                    const lMetaData = metadata;
                    const appParams = filterRequestParams(requestParams, includeOptions, excludeParams);
                    const attachJSON = false;
                    const itemId = null;
                    const reqId = uuidv1();
                    const param = { ...appParams};

                    const fn = (resolve, reject) => {
                        this.newMapInfoRequest(reqId, param);
                        const response = getFeatureInfo(basePath, param, layer, {attachJSON, itemId})
                            .then((response) => {
                                (response.data.exceptions || response.data.exception)
                                ? this.exceptionsFeatureInfo(
                                        reqId,
                                        response.data.exceptions || response.data.exception,
                                        requestParams,
                                        lMetaData
                                    )
                                : this.loadFeatureInfo(
                                    reqId,
                                    response.data,
                                    requestParams,
                                    { ...lMetaData, features: response.features, featuresCrs: response.featuresCrs },
                                    null // layer - removi o parametro LAYER por causa da reatividade que causa no mapa!
                                )
                                return true;
                            })
                            .catch((e) => {
                                this.errorFeatureInfo(
                                    reqId,
                                    e.data || e.statusText || e.status || e.message,
                                    requestParams,
                                    lMetaData
                                );
                                return false;
                            });
                            resolve(response);
                    };

                    if(this.identifySequential) {
                        promises.push(async () => new Promise((resolve, reject) => fn(resolve, reject)));
                    }else{
                        promises.push(new Promise((resolve, reject) => fn(resolve, reject)));
                    }
                }
            });

            if(this.identifySequential) {
                return await this.executTasksSequential(promises);
            }else{
                return await Promise.all(promises)
                .then(responses => {
                    // Get a result object from each of the responses
                    return Promise.all(responses.map(response => {
                        return response;
                    }));
                })
                .then(results => {
                    return results;
                });
            }
        },
        async executTasksSequential(tasks) {
            for (const task of tasks) {
                const result = await task();
            }
        },
        setInfoMarker(coordinate=null){
            const _map = this.getMap();
            if(!_map) return;

            let sourceId = this.options.sourceId;
            let layerId = this.options.layerId;

            let _source = _map.getSource(sourceId);
            let _layer = _map.getLayer(layerId);
            if(coordinate){
                let lng = coordinate[0];
                let lat = coordinate[1];

                if(!_layer){
                    if(!_source){
                        _source = _map.createVectorSource({
                            features: []
                        });
                        _map.setSource(sourceId, _source);
                    }
                    _layer = _map.createVectorLayer({
                        source: _source,
                        zIndex: this.options.zindex,
                    });
                    _map.setLayer(layerId, _layer);
                    _map.addlayerToMap({layer: _layer},true);
                }

                _map.clearSource(sourceId);

                let feature = _map.createFeature({
                    id: 99999,
                    longitude: lng,
                    latitude: lat,
                    style: this.getStyleMark()
                });
                if(feature){
                    _source.addFeature(feature);
                    // ANIMATE TO CENTER IF IN RIGTH SIDE
                    let view = _map.getOLMapView();
                    let center = view.getCenter();
                    if(lng > center[0]){
                        let resolution = view.getResolution();
                        view.animate({zoom: view.getZoom()}, {center: [lng + 30*resolution, lat]})
                    }
                }
            }else{
                if(_layer){
                    _map.clearSource(sourceId);
                    _map.removeLayer(_layer);
                    _map.setLayer(layerId, null);
                    _map.setSource(sourceId, null);
                }
            }
        },
        getStyleMark() {
            const _map = this.getMap();
            if(!_map) return null;

            return _map.createStyle({
                cursor: 'pointer',
                image: _map.createIcon({
                    src: require(`@modules/${this.moduleCode}/images/marker-identify.png`),
                    anchor: [.5, 1],
                    scale: 1,
                })
            });
        },
        setCoordinatesInfo(params) {
            const {coordinate, projection, resolution} = params;
            this.coordinatesInfo = JSON.parse(JSON.stringify({
                coordinate,
                projection,
                resolution
            }));
        }
    },
    watch: {
        mapInitialized: {
            immediate: false,
            handler(value, oldVal) {
                this.setMapInteraction(value);
            },
        },
        showModal: {
            immediate: false,
            handler(value, oldVal) {
                if(value){
                    this.initialize();
                }
            },
        },
        isPanelCatalogVisible: {
            immediate: false,
            handler(value, oldVal) {
                if(value){
                    this.closeModal();
                }
            },
        },
        actionMap: {
            immediate: true,
            deep: true,
            handler(value, oldVal) {
                this.setActiveMapInteraction(!!!value);
            },
        },
    },
};
</script>

<style scoped lang="scss">
.flex-scroll {
    overflow-y: auto;
    overflow-x: hidden;
    min-height:0;
    -webkit-box-flex:1;
    flex:1;
}
.identify-panel {
    .identify-results {
        &.loading {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            > div {
                overflow: 'hidden';
                width: 176px;
                height: 176px;
            }
        }
    }
}



</style>